import React, { useEffect, useCallback, useRef } from 'react';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { css } from '@emotion/react';

import { useSelector } from 'react-redux';
import { getNovoAIProperties } from 'redux/selectors/lecture-page';
import { NovoAIContentType, NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { getAIGeneratedActivity,
  getAiSuggestSummaryAndTakeaway,
  setNovoAIShowGenerateModal,
} from 'redux/actions/lecture-pages';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { wrapThunkAction } from 'redux/utils';

import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { shortBar } from 'styles/global_defaults/scaffolding';
import AiGeneratingLoader from './ai-generating-loader';
import AiGeneratedPreview from './ai-generated-preview';
import { lhsCommonStyles } from '../suggest-activities/styles';

const styles = css`
  padding-left: ${shortBar}px;
  padding-right: ${shortBar}px;
  ${lhsCommonStyles}
`;

interface AbortableRequest {
  abort(): void;
}

const AiGeneratingModalContent = () => {
  const novoAIProperties = useSelector(getNovoAIProperties);
  const isLoaded = useSelector(state => state.app.aiSuggestComponent.isLoaded);

  const {
    content,
    itemType,
    lengthText,
    tone,
    itemTrueType,
  } = novoAIProperties;

  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const requestRef = useRef<AbortableRequest | null>(null);

  const getScope = useCallback(() => {
    switch (content?.type) {
      case NovoAIContentType.SECTION:
        return 'section';
      case NovoAIContentType.SUBSECTION:
        return 'sub_section';
      default:
        return 'page';
    }
  }, [content?.type]);

  const getType = useCallback(() => {
    switch (itemTrueType) {
      case ComponentType.TEXT_WITH_IMAGE_TOP:
        return 'blurb_top_image';
      case ComponentType.TEXT_WITH_IMAGE_BKG:
        return 'blurb_background_image';
      case ComponentType.TEXT_WITH_IMAGE_SIDE:
        return 'blurb_side_image';
      default:
        return 'rich_text';
    }
  }, [itemTrueType]);

  const showErrorAlertAndCloseModal = useCallback((errorCode) => {
    let errorHeader = t.LECTURE_PAGES.LHS.NOVO_AI.ALERT.ERROR.TITLE();
    let errorMessage = '';

    if (['content_too_short', 'blank_text_extract'].includes(errorCode)) {
      errorMessage = t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_MESSAGES.TOO_SHORT();
    } else if (errorCode === 'content_too_long') {
      errorMessage = t.LECTURE_PAGES.COMPONENTS.DISCUSSION.GENERATED_MESSAGES.TOO_LONG();
    } else {
      errorHeader = t.FORM.OOPS();
      errorMessage = t.FORM.ERROR_SOMETHING_WRONG_SHORT();
    }

    dispatch(
      addAlertMessage({
        type: AlertMessageType.ERROR,
        header: errorHeader,
        message: errorMessage,
      }),
    );
    dispatch(setNovoAIShowGenerateModal(false));
  }, [dispatch]);

  useEffect(() => {
    if ([NovoAIItemType.SUMMARY, NovoAIItemType.KEY_TAKEAWAYS].includes(itemType)) {
      const request = dispatch(getAiSuggestSummaryAndTakeaway({
        catalogId: params.catalogId,
        lecturePageId: params.lecturePageId,
        activityType: itemType,
        contentOptions: {
          scope: getScope(),
          type: getType(),
          tone,
          length: lengthText,
        },
      }));

      requestRef.current = request;

      wrapThunkAction(request).catch((error) => {
        if (error?.error?.name !== 'AbortError') {
          showErrorAlertAndCloseModal(error.payload?.code);
        }
      });
    } else if ([
      NovoAIItemType.POLL,
      NovoAIItemType.ASSIGNMENT,
      NovoAIItemType.PUBLIC_FEEDBACK,
      NovoAIItemType.PRE_SURVEY,
      NovoAIItemType.POST_SURVEY,
    ].includes(itemType)) {
      const request = dispatch(getAIGeneratedActivity({
        activityType: itemType,
      }));

      requestRef.current = request;

      wrapThunkAction(request).catch((error) => {
        if (error?.error?.name !== 'AbortError') {
          showErrorAlertAndCloseModal(error.payload?.code);
        }
      });
    }

    // Cleanup function to abort requests when modal closes
    return () => {
      if (requestRef.current && typeof requestRef.current.abort === 'function') {
        requestRef.current.abort();
      }
    };
  }, [
    dispatch,
    itemType,
    params.catalogId,
    params.lecturePageId,
    getScope,
    getType,
    tone,
    lengthText,
    showErrorAlertAndCloseModal,
  ]);

  return (
    <div css={styles}>
      {isLoaded ? (
        <AiGeneratedPreview />
      ) : (
        <AiGeneratingLoader
          customDescription={t.LECTURE_PAGES.LHS.NOVO_AI.SUGGEST_ACTIVITIES.SINGLE_SUGGEST_DESCRIPTION()}
        />
      )}
    </div>
  );
};

export default AiGeneratingModalContent;
